/**
 * Purpose: Accessible dialog focus management (WCAG 2.4.3 / 2.1.2). When active,
 *          moves focus into the container, keeps Tab/Shift+Tab cycling WITHIN it
 *          (no escaping to the background), and restores focus to the previously
 *          focused element when it deactivates. Reusable across all modals.
 * Inputs: active (boolean).
 * Outputs: a ref to attach to the dialog container element.
 *
 * Presentational only — no app/data concerns. Pairs with each modal's own
 * Escape/backdrop close handling (this hook does not close anything).
 */
import { useCallback, useEffect, useRef } from 'react';

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

const useFocusTrap = (active) => {
  const containerRef = useRef(null);
  const previouslyFocused = useRef(null);

  const focusables = useCallback(() => {
    const node = containerRef.current;
    if (!node) return [];
    // The selector already excludes disabled and tabindex="-1". We intentionally
    // do NOT filter by layout visibility (offsetParent/getClientRects) — those
    // are unreliable for fixed-position dialogs and unavailable without a layout
    // engine; hidden elements simply no-op on focus().
    return Array.from(node.querySelectorAll(FOCUSABLE));
  }, []);

  useEffect(() => {
    if (!active) return undefined;
    const node = containerRef.current;
    if (!node) return undefined;

    previouslyFocused.current = document.activeElement;

    // Move focus inside: first focusable, else the container itself.
    const items = focusables();
    (items[0] || node).focus?.();

    const onKeyDown = (event) => {
      if (event.key !== 'Tab') return;
      const items2 = focusables();
      if (items2.length === 0) {
        event.preventDefault();
        node.focus?.();
        return;
      }
      const first = items2[0];
      const last = items2[items2.length - 1];
      const activeEl = document.activeElement;
      if (event.shiftKey && (activeEl === first || !node.contains(activeEl))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && activeEl === last) {
        event.preventDefault();
        first.focus();
      }
    };

    node.addEventListener('keydown', onKeyDown);
    return () => {
      node.removeEventListener('keydown', onKeyDown);
      // Restore focus to where it was before the dialog opened.
      const prev = previouslyFocused.current;
      if (prev && typeof prev.focus === 'function') prev.focus();
    };
  }, [active, focusables]);

  return containerRef;
};

export default useFocusTrap;
